/* ============================================================================
 * qring-variant-gating.js  —  Custom variant gating for the Q-ring product
 * ----------------------------------------------------------------------------
 * Isolated custom code for the Impact (Maestrooo) theme. Kept in its own asset
 * so Maestrooo theme updates never overwrite it. The <script> tag that loads it
 * lives in snippets/js-variables.liquid, guarded by request.page_type == 'product'.
 * It activates only for the MAIN product's
 * variant picker(s): those found inside .shopify-section--main-product (plus any
 * form id explicitly pushed into window.QRingGate.formIds).
 *
 * Behaviour:
 *   1. No variant pre-selected on load: every Couleur/Taille radio is unchecked.
 *   2. Add-to-cart AND the dynamic checkout button stay locked until BOTH a
 *      colour AND a size are chosen. While locked for an incomplete selection the
 *      submit button reads "Choisissez couleur et taille".
 *   3. Once both are chosen, Impact's own (unmodified) variant flow resolves the
 *      variant, sets the gallery/price, and we commit the real variant id on the
 *      hidden input so the native add-to-cart works exactly as before.
 *   4. On load (nothing selected) the gallery shows the first neutral / hero
 *      image instead of the black-ring variant image. Picking a colour (even
 *      before a size) immediately previews that colour's photo.
 *
 * Design note — why this does not break add-to-cart:
 *   Impact resolves a variant server-side for ANY set of checked options, even a
 *   partial one (it auto-completes the missing option to the first available
 *   variant). That is exactly what makes "require both options" hard and what
 *   silently re-selected a size last time. So while the selection is incomplete
 *   we intercept the radio `change` event in the CAPTURE phase and call
 *   stopImmediatePropagation(): Impact's delegated handler never runs, nothing is
 *   auto-completed, the gallery stays neutral and the buttons stay locked. As
 *   soon as both options are picked we let the event flow through untouched, so
 *   the critical add-to-cart path is 100% native Impact code.
 * ========================================================================== */
(function () {
  "use strict";

  var cfg = (window.QRingGate = window.QRingGate || { formIds: [] });
  if (cfg.__inited) return;
  cfg.__inited = true;

  var LOCK_CLASS = "qr-gate-locked";
  var gatedForms = []; // resolved <form> elements we manage

  /* ----------------------------------------------------------------- helpers */
  function cssEsc(s) {
    return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/"/g, '\\"');
  }
  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }
  // IMPORTANT: this <form> contains <input name="id">, so `form.id` is DOM-clobbered
  // and returns that input element, NOT the form's id string. Never read/write form.id.
  // We stash the real form id string here instead.
  function fid(form) {
    return form && form.__qrFormId;
  }
  function getForm(formId) {
    var el = document.getElementById(formId);
    if (el && el.tagName === "FORM") return el;
    try {
      var c = document.forms[formId];
      if (c && c.tagName === "FORM") return c;
    } catch (e) {}
    return null;
  }
  function optionInputs(formId) {
    return toArray(
      document.querySelectorAll('input[data-option-position][form="' + cssEsc(formId) + '"]')
    );
  }
  function distinctPositions(inputs, onlyChecked) {
    var set = {};
    inputs.forEach(function (i) {
      if (onlyChecked && !i.checked) return;
      set[i.getAttribute("data-option-position")] = true;
    });
    return Object.keys(set);
  }
  function isComplete(formId) {
    var inputs = optionInputs(formId);
    if (!inputs.length) return true; // nothing to gate
    return distinctPositions(inputs, true).length >= distinctPositions(inputs, false).length;
  }
  function hiddenIdInput(form) {
    try {
      var el = form.elements.namedItem("id");
      // make sure we got the variant input, not the form's id attribute
      if (el && el.tagName === "INPUT") return el;
    } catch (e) {}
    return form.querySelector('input[name="id"]');
  }
  function buyButtonsBlocks(form) {
    // The [data-block-type="buy-buttons"] block element survives Impact's partial
    // re-renders (only its inner <buy-buttons> gets swapped), so it is the safe
    // place to hang the lock class. The product form lives inside this block.
    return toArray(document.querySelectorAll('[data-block-type="buy-buttons"]')).filter(function (b) {
      return b.contains(form);
    });
  }
  function submitButtons(form) {
    var inside = toArray(form.querySelectorAll('button[type="submit"], [type="submit"]'));
    var external = toArray(
      document.querySelectorAll('[type="submit"][form="' + cssEsc(fid(form)) + '"]')
    );
    return inside.concat(external);
  }

  /* ------------------------------------------------------------------- lock UI */
  var LOCKED_LABEL = "Choisissez couleur et taille";
  // Impact's button is <button><div>Ajouter au panier</div><span class="button__loader">..</span></button>;
  // the label lives in the first child <div>.
  function labelEl(btn) {
    return btn.querySelector(":scope > div") || btn;
  }
  function applyButtonLabel(form, locked) {
    // Only relabel when locked because the selection is INCOMPLETE. When locked for
    // another reason (e.g. a chosen combination is sold out) we leave the server label.
    var incomplete = !isComplete(fid(form));
    submitButtons(form).forEach(function (btn) {
      var el = labelEl(btn);
      if (locked && incomplete) {
        if (el.__qrOrigLabel == null) el.__qrOrigLabel = el.textContent;
        if (el.textContent !== LOCKED_LABEL) el.textContent = LOCKED_LABEL;
      } else if (el.__qrOrigLabel != null) {
        el.textContent = el.__qrOrigLabel;
        el.__qrOrigLabel = null;
      }
    });
  }
  function setLocked(form, locked) {
    form.__qrLocked = !!locked;
    buyButtonsBlocks(form).forEach(function (b) {
      b.classList.toggle(LOCK_CLASS, !!locked);
    });
    submitButtons(form).forEach(function (btn) {
      if (locked) btn.setAttribute("aria-disabled", "true");
      else btn.removeAttribute("aria-disabled");
    });
    applyButtonLabel(form, !!locked);
  }

  /* ----------------------------------------------------------- initial reset */
  function clearLegend(input) {
    try {
      var fs = input.closest("fieldset.variant-picker__option");
      if (!fs) return;
      var span = fs.querySelector(".variant-picker__option-info legend + span");
      if (span) span.textContent = "";
    } catch (e) {}
  }
  function setLegendFromInput(input) {
    try {
      var fs = input.closest("fieldset.variant-picker__option");
      if (!fs) return;
      var span = fs.querySelector(".variant-picker__option-info legend + span");
      if (!span) return;
      var label = document.querySelector('label[for="' + cssEsc(input.id) + '"]');
      span.textContent = label ? label.textContent.trim() : "";
    } catch (e) {}
  }
  function resetSelection(form) {
    var inputs = optionInputs(fid(form));
    inputs.forEach(function (i) {
      if (i.checked) i.checked = false; // programmatic -> does NOT fire change
      clearLegend(i);
    });
    var idInput = hiddenIdInput(form);
    if (idInput) idInput.value = ""; // no variant bound while nothing is chosen
  }

  /* --------------------------------------------------------- gallery neutral
   * On load (nothing selected) we want a neutral / hero image, not the black-ring
   * variant image. Maestrooo's "variant image set" hack tags each colour image with
   * an alt like "#couleur_noir". On load Shopify pre-selects Noir, so the Noir image
   * is the visible one (Or/Argent are already hidden by the theme's filtered-indexes).
   * We simply hide the still-visible colour-tagged image(s) so the first NEUTRAL media
   * (alt without a leading '#') becomes the one shown.
   *
   * We never need to un-hide manually: as soon as a full variant is chosen, Impact
   * dispatches product:rerender -> "carousel:filter" -> _filterItems(), which rewrites
   * item.hidden for EVERY media based on the chosen colour, overriding our flag. */
  function neutralGallery(formId, attempt) {
    attempt = attempt || 0;
    var gallery = document.querySelector('product-gallery[form="' + cssEsc(formId) + '"]');
    if (!gallery) return;
    var medias = toArray(gallery.querySelectorAll(".product-gallery__media"));

    // Bail out if hiding the colour images would leave the gallery empty.
    var hasNeutral = medias.some(function (m) {
      if (m.getAttribute("data-media-type") !== "image") return false;
      var img = m.querySelector("img");
      return ((img && img.getAttribute("alt")) || "").charAt(0) !== "#";
    });
    if (!hasNeutral) return;

    medias.forEach(function (m) {
      if (m.hasAttribute("hidden")) return; // already hidden by the theme (other colours)
      var img = m.querySelector("img");
      var alt = (img && img.getAttribute("alt")) || "";
      if (alt.charAt(0) === "#") {
        m.setAttribute("hidden", "");
        m.setAttribute("data-qr-hidden", "1"); // marker only; Impact controls hidden afterwards
      }
    });

    // Point the carousel's internal index at the first visible (neutral) media so
    // thumbnails / dots stay consistent. Retry until <media-carousel> is upgraded.
    var carousel = gallery.querySelector("media-carousel");
    if (!carousel || typeof carousel.select !== "function") {
      if (attempt < 30) return window.requestAnimationFrame(function () { neutralGallery(formId, attempt + 1); });
      return;
    }
    var firstVisible = -1;
    for (var k = 0; k < medias.length; k++) {
      if (!medias[k].hasAttribute("hidden")) { firstVisible = k; break; }
    }
    if (firstVisible >= 0) {
      try { carousel.select(firstVisible, { animate: false }); } catch (e) {}
    }
  }

  /* ------------------------------------------------- colour image preview ---
   * When only the colour is chosen (selection still incomplete, so we block
   * Impact), we still want the gallery to show that colour's photo immediately.
   * We do it ourselves — reveal the matching "#couleur_x" media, hide the other
   * colours, move the carousel to it — WITHOUT triggering Impact's variant
   * resolution, so the add-to-cart button stays locked until a size is picked.
   * If the changed option isn't a colour with a matching image (e.g. a size),
   * we leave the gallery untouched. */
  function normColor(s) {
    // lowercase, drop accents (NFD splits them out) and any non-alphanumeric char
    return (s || "").toLowerCase().normalize("NFD").replace(/[^a-z0-9]/g, "");
  }
  function colourPartOfAlt(alt) {
    // "#couleur_noir" -> "noir"
    return normColor(String(alt).split("_").pop());
  }
  function previewColourForInput(form, input) {
    try {
      var label = document.querySelector('label[for="' + cssEsc(input.id) + '"]');
      var wanted = normColor(label ? label.textContent : "");
      if (!wanted) return;
      var gallery = document.querySelector('product-gallery[form="' + cssEsc(fid(form)) + '"]');
      if (!gallery) return;
      var medias = toArray(gallery.querySelectorAll(".product-gallery__media"));

      // Only act if the chosen value maps to a colour image (otherwise it's a size, etc.)
      var hasMatch = medias.some(function (m) {
        var img = m.querySelector("img");
        var alt = (img && img.getAttribute("alt")) || "";
        return alt.charAt(0) === "#" && colourPartOfAlt(alt) === wanted;
      });
      if (!hasMatch) return;

      var targetIdx = -1;
      medias.forEach(function (m, i) {
        var img = m.querySelector("img");
        var alt = (img && img.getAttribute("alt")) || "";
        if (alt.charAt(0) !== "#") return; // leave neutral media alone
        if (colourPartOfAlt(alt) === wanted) {
          m.removeAttribute("hidden");
          m.removeAttribute("data-qr-hidden");
          if (targetIdx === -1) targetIdx = i;
        } else {
          m.setAttribute("hidden", ""); // hide the other colours
        }
      });
      if (targetIdx >= 0) {
        var carousel = gallery.querySelector("media-carousel");
        if (carousel && typeof carousel.select === "function") {
          try { carousel.select(targetIdx, { animate: false }); } catch (e) {}
        }
      }
    } catch (e) {}
  }

  /* --------------------------------------------------------- event handlers */
  // Capture-phase: runs before Impact's delegated body listener.
  function onOptionChangeCapture(event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches("input[data-option-position]")) return;
    var form = input.form;
    if (!form || gatedForms.indexOf(form) === -1) return;

    if (!isComplete(fid(form))) {
      // Block Impact's partial auto-resolution; just reflect this choice.
      event.stopImmediatePropagation();
      setLegendFromInput(input);
      setLocked(form, true);
      previewColourForInput(form, input); // show the chosen colour's photo right away
    }
    // else: complete -> let Impact resolve natively; we unlock on variant:change.
  }

  function onVariantChange(event) {
    var form = event.target;
    if (!form || gatedForms.indexOf(form) === -1) return;
    var variant = event.detail && event.detail.variant;
    var idInput = hiddenIdInput(form);
    if (idInput) {
      idInput.disabled = false;
      idInput.value = variant && variant.id ? variant.id : "";
    }
    if (variant && variant.id && variant.available !== false) setLocked(form, false);
    else setLocked(form, true);
  }

  function onSubmitCapture(event) {
    // Belt-and-braces: never let the form submit while locked (covers the ATC
    // button; the dynamic checkout button is blocked via pointer-events in CSS).
    var form = event.currentTarget;
    if (form && form.__qrLocked) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  /* -------------------------------------------------------------- bootstrap */
  function injectStyles() {
    if (document.getElementById("qr-gate-styles")) return;
    var css =
      '[data-block-type="buy-buttons"].' + LOCK_CLASS + " button[type=\"submit\"]," +
      '[data-block-type="buy-buttons"].' + LOCK_CLASS + " .shopify-payment-button{" +
      "opacity:.4;cursor:not-allowed;pointer-events:none;filter:grayscale(45%);}";
    var style = document.createElement("style");
    style.id = "qr-gate-styles";
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  function initForm(formId) {
    var form = getForm(formId);
    if (!form || gatedForms.indexOf(form) !== -1) return;
    if (!optionInputs(formId).length) return; // single-variant product: nothing to gate
    form.__qrFormId = formId; // stash the id string (form.id is DOM-clobbered; see fid())
    gatedForms.push(form);
    resetSelection(form);
    setLocked(form, true);
    form.addEventListener("submit", onSubmitCapture, true);
    neutralGallery(formId, 0);
  }

  function discoverFormIds() {
    var ids = (cfg.formIds || []).slice();
    toArray(document.querySelectorAll(".shopify-section--main-product variant-picker[form-id]")).forEach(
      function (vp) {
        var id = vp.getAttribute("form-id");
        if (id && ids.indexOf(id) === -1) ids.push(id);
      }
    );
    return ids;
  }

  function init() {
    injectStyles();
    discoverFormIds().forEach(initForm);
    // Global listeners (survive Impact's DOM re-renders).
    document.addEventListener("change", onOptionChangeCapture, true);
    document.addEventListener("variant:change", onVariantChange);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
